const CLOCK_MS = 30000;

function formatShopClock(timeZone) {
  const now = new Date();
  const weekday = new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    timeZone,
  })
    .format(now)
    .toUpperCase();
  const time = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone,
  })
    .format(now)
    .replace(/\s/g, ' ')
    .toUpperCase();
  return `${weekday} — ${time}`;
}

function bootClock(root) {
  const el = root.querySelector('[data-shop-clock]');
  if (!el) return () => {};
  const timeZone = el.getAttribute('data-timezone') || 'Asia/Karachi';
  const tick = () => {
    try {
      el.textContent = formatShopClock(timeZone);
    } catch {
      /* keep the Liquid fallback */
    }
  };
  tick();
  const id = window.setInterval(tick, CLOCK_MS);
  return () => window.clearInterval(id);
}

function bootReveal(root) {
  const cards = [...root.querySelectorAll('.artink-shop__card')];
  if (!cards.length) return () => {};
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    cards.forEach((card) => card.classList.add('is-in'));
    return () => {};
  }
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        io.unobserve(entry.target);
      });
    },
    { threshold: 0.16, rootMargin: '0px 0px -8% 0px' }
  );
  cards.forEach((card) => io.observe(card));
  return () => io.disconnect();
}

function selectGiftFromCard(card) {
  const gift = document.querySelector('[data-artink-gift]');
  if (!gift || !card?.dataset.giftTitle) return false;
  document.dispatchEvent(
    new CustomEvent('artink:gift-select', {
      detail: {
        title: card.dataset.giftTitle,
        cents: card.dataset.giftCents,
        image: card.dataset.giftImage,
        variantId: card.dataset.giftVariant,
      },
    })
  );
  gift.scrollIntoView({ behavior: 'smooth', block: 'start' });
  return true;
}

const FLY_MS = 850;
const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* The visible header cart button, or null when it is off screen. */
function cartTarget() {
  const candidates = [
    document.querySelector('[data-artink-header] [data-cart-toggle]'),
    document.querySelector('cart-icon'),
  ];
  for (const el of candidates) {
    if (!el) continue;
    const rect = el.getBoundingClientRect();
    if (rect.width && rect.height && rect.bottom > 0 && rect.top < window.innerHeight) return { el, rect };
  }
  return null;
}

/* A round thumbnail of the product arcs from the card into the cart icon. */
function flyToCart(card) {
  const frame = card?.querySelector('.artink-shop__frame');
  const target = cartTarget();
  if (!frame || !target || prefersReducedMotion() || !document.body.animate) return Promise.resolve();

  const from = frame.getBoundingClientRect();
  const size = Math.round(Math.min(120, Math.max(56, from.width * 0.42)));
  const img = card.querySelector('.artink-shop__img--main');

  const ghost = document.createElement('div');
  ghost.className = 'artink-fly';
  ghost.setAttribute('aria-hidden', 'true');
  ghost.style.width = `${size}px`;
  ghost.style.height = `${size}px`;
  if (img instanceof HTMLImageElement && (img.currentSrc || img.src)) {
    const clone = new Image();
    clone.src = img.currentSrc || img.src;
    clone.alt = '';
    ghost.append(clone);
  }
  document.body.append(ghost);

  const sx = from.left + from.width / 2 - size / 2;
  const sy = from.top + from.height / 2 - size / 2;
  const ex = target.rect.left + target.rect.width / 2 - size / 2;
  const ey = target.rect.top + target.rect.height / 2 - size / 2;
  const mx = sx + (ex - sx) * 0.55;
  const my = Math.min(sy, ey) - Math.min(160, Math.abs(sy - ey) * 0.35 + 60);

  const animation = ghost.animate(
    [
      { transform: `translate(${sx}px, ${sy}px) scale(0.5)`, opacity: 0, offset: 0 },
      { transform: `translate(${sx}px, ${sy - 24}px) scale(1)`, opacity: 1, offset: 0.2 },
      { transform: `translate(${mx}px, ${my}px) scale(0.62) rotate(-10deg)`, opacity: 1, offset: 0.62 },
      { transform: `translate(${ex}px, ${ey}px) scale(0.16) rotate(0deg)`, opacity: 0.5, offset: 1 },
    ],
    { duration: FLY_MS, easing: 'cubic-bezier(0.45, 0, 0.25, 1)', fill: 'forwards' }
  );
  return animation.finished.catch(() => {}).finally(() => ghost.remove());
}

/* The cart icon bounces and sends out a soft ring once the add succeeds. */
function bumpCart() {
  const target = cartTarget();
  if (!target || prefersReducedMotion() || !document.body.animate) return;
  target.el.animate(
    [{ transform: 'scale(1)' }, { transform: 'scale(1.25)' }, { transform: 'scale(0.92)' }, { transform: 'scale(1)' }],
    { duration: 560, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
  );
  const ring = document.createElement('span');
  ring.className = 'artink-fly-ring';
  ring.setAttribute('aria-hidden', 'true');
  ring.style.left = `${target.rect.left + target.rect.width / 2}px`;
  ring.style.top = `${target.rect.top + target.rect.height / 2}px`;
  document.body.append(ring);
  ring
    .animate(
      [
        { transform: 'translate(-50%, -50%) scale(0.4)', opacity: 0.7 },
        { transform: 'translate(-50%, -50%) scale(2.2)', opacity: 0 },
      ],
      { duration: 700, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' }
    )
    .finished.catch(() => {})
    .finally(() => ring.remove());
}

/* Resolves the cart-update event name once; the animation still plays without it. */
let cartEventName = null;
const cartEventReady = import('@shopify/events')
  .then(({ StandardEvents }) => {
    cartEventName = StandardEvents?.cartLinesUpdate || null;
  })
  .catch(() => {});

function bootForms(root) {
  // Sections like Collection tabs opt out so their buttons add straight to the cart.
  const giftHandoff = root.dataset.giftHandoff !== 'false';
  const onClick = (event) => {
    const btn = event.target.closest('.artink-shop__btn');
    if (!btn) return;
    // Sold-out cards must not be handed to the gift builder.
    if (btn.matches('.artink-shop__btn--sold, [aria-disabled="true"], :disabled')) {
      event.preventDefault();
      return;
    }
    const card = btn.closest('.artink-shop__card');
    if (!giftHandoff || !selectGiftFromCard(card)) return;
    event.preventDefault();
    event.stopPropagation();
  };
  const onSubmit = (event) => {
    const card = event.target.closest('.artink-shop__card');
    if (giftHandoff && selectGiftFromCard(card)) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    const btn = event.target.querySelector('.artink-shop__btn');
    if (!btn || btn.tagName !== 'BUTTON') return;
    btn.setAttribute('aria-busy', 'true');
    window.setTimeout(() => btn.removeAttribute('aria-busy'), 1600);
    if (!card) return;
    card._artinkFlight = flyToCart(card);
    // Without the cart event (module unavailable), bump once the flight lands.
    if (!cartEventName) card._artinkFlight.then(bumpCart);
  };
  // Bump the cart only after the add really succeeded.
  const onCartUpdate = (event) => {
    const card = event.target instanceof Element ? event.target.closest('.artink-shop__card') : null;
    if (!card || !event.promise) return;
    const flight = card._artinkFlight || Promise.resolve();
    event.promise
      .then(async (result) => {
        if (result?.detail?.didError) return;
        await flight;
        bumpCart();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[artink-shop] cart update failed', error);
      });
  };
  let listenedEvent = null;
  let disposed = false;
  cartEventReady.then(() => {
    if (disposed || !cartEventName) return;
    listenedEvent = cartEventName;
    root.addEventListener(listenedEvent, onCartUpdate);
  });
  root.addEventListener('click', onClick, true);
  root.addEventListener('submit', onSubmit, true);
  return () => {
    root.removeEventListener('click', onClick, true);
    root.removeEventListener('submit', onSubmit, true);
    disposed = true;
    if (listenedEvent) root.removeEventListener(listenedEvent, onCartUpdate);
  };
}

function bootShop(root) {
  if (!root || root.dataset.booted === '1') return;
  root.dataset.booted = '1';
  const cleanups = [bootClock(root), bootReveal(root), bootForms(root)];
  root._artinkShopCleanup = () => {
    cleanups.forEach((fn) => fn && fn());
    root.dataset.booted = '0';
  };
}

function initAll() {
  document.querySelectorAll('[data-artink-shop]').forEach(bootShop);
}

initAll();
document.addEventListener('shopify:section:load', (event) => {
  event.target.querySelectorAll('[data-artink-shop]').forEach(bootShop);
});
document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('[data-artink-shop]').forEach((root) => {
    root._artinkShopCleanup?.();
  });
});
